import styled from "@emotion/styled"
import { Desktop, Mobile } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "@eurodrive/utils"
import { faqInteraction } from "@eurodrive/utils/gtm"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material"
import { CarSpec, DetailedVehicleImages, LeasingCarDto } from "api"
import Image from "next/image"
import { useState } from "react"
import SpinCarWidget from "./SpinCarWidget"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 56px;
  padding-bottom: 64px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 515px 1fr;
    gap: 80px;
    padding-top: 112px;
    padding-bottom: 112px;
  }
`

const ImageContainer = styled.div`
  position: relative;
  height: 348px;
  grid-row: 2;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 515px;
    grid-row: 1;
  }
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`

const StyledAccordion = styled(Accordion)`
  padding: 0 !important;
  box-shadow: none !important;
  background-color: transparent;
  border-bottom: 1px solid #ece9e9;

  border-radius: 0 !important;
  ::before {
    display: none;
  }
  & path {
    stroke: white;
  }
  &.Mui-expanded {
    margin: 0 !important;
  }
  & .MuiAccordionSummary-root {
    min-height: 0 !important;
    height: 56px;
    /* border-bottom: 1px solid #aaa !important; */
  }
  & .MuiAccordionSummary-content {
    margin: 0 !important;
  }

  & .MuiAccordionDetails-root {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0;
    padding-bottom: 16px;
  }
`

const AccordionColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const AccordionItemsColumns = styled.ul`
  display: flex;
  gap: 8px;
  flex-direction: column;
  margin: 0;
  padding: 0;
`

const AccordionItem = styled.li`
  display: flex;
  gap: 5px;
  align-items: center;
`

const CardsGrid = styled.div<{ secondHand: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  /* width: fit-content; */
`

const Card = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #ece9e9;
  width: 102px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 32px 24px;
    /* gap: 24px; */
    height: 108px;
    /* width: 130px; */
    width: 100%;
  }
`

const IconbBox = styled.div`
  width: 40px;
  height: 40px;
  padding: 6px 3px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
  }
`

const CardTextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* gap: 4px; */
  text-align: center;
`

export default function AccordionComponent({
  data,
  showSpinCar,
  detailedImages,
  leasingData,
  leasingImage,
  vehicleInfoBlockMapped,
}: {
  data: CarSpec
  showSpinCar?: boolean
  detailedImages: DetailedVehicleImages
  leasingData?: LeasingCarDto
  leasingImage?: any
  vehicleInfoBlockMapped?: {
    infoTitle?: string
    disclaimer?: string
  }[]
}) {
  const [expanded, setExpanded] = useState<string | false>(
    leasingData ? "panel0" : "panel1",
  )

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        // Determine FAQ question title based on panel
        const panelTitles: Record<string, string> = {
          panel0: "מפרט טכני",
          panel1: "תיאור",
          panel2: "אבזור",
          panel3: "צבעים",
        }
        faqInteraction(panelTitles[panel] || panel, "car_page")
      }
      setExpanded(isExpanded ? panel : false)
    }

  const q1 = [
    `האצה מ-0 ל-100 - ${data.accelerationTo100} ש׳`,
    `רמת אבזור בטיחותי - ${data.safetyLevel}`,
    `טווח נסיעה - ${data.motorRange} ק״מ`,
    `קיבולת סוללה - ${data.batteryCapacityKWh} קוט״ש`,
    `מספר דלתות - ${data.doorsNumber}`,
    `מספר נוסעים (כולל נהג) - ${data.seatsNumber}`,
    `סוג מנוע - ${data.engineType}`,
    `הספק (כוח סוס) - ${data.enginePower} כ״ס`,
    ...(data.afterMarkerSafetySystem
      ? [
          data.afterMarkerSafetySystem === "yes"
            ? "חבילות בטיחות מהיצרן"
            : `חבילות בטיחות מהיצרן - ${data.afterMarkerSafetySystem}`,
        ]
      : []),
  ]
  const q2 = [
    `חלונות חשמל - ${data.powerWindowNumber}`,
    `חיישני חניה - ${data.proximitySensors}`,
    data.camera,
    data.acc === "yes" ? "בקרת שיוט" : undefined,
  ].filter(Boolean) as string[]
  const q3 = [
    ...(data.abs === "yes" ? ["ABS"] : []),
    ...(data.esp === "yes" ? ["בקרת יציבות"] : []),
    `כריות אוויר - ${data.airbagsNumber}`,
  ]
  const cu17Image = detailedImages?.images.find((el) =>
    el.url.includes("cu-17"),
  )
  const items = [
    {
      icon: "/icons/displacement.svg",
      title: "נפח מנוע",
      value: leasingData?.engineCapacity
        ? `${leasingData.engineCapacity}`
        : undefined,
    },
    {
      icon: "/icons/engineType.svg",
      title: "סוג מנוע",
      value: leasingData?.carZone?.spec?.engineType,
    },
    {
      icon: "/icons/gearbox.svg",
      title: "תיבת הילוכים",
      value: leasingData?.carZone?.spec?.transmissionType,
    },
    {
      icon: "/icons/pollution.svg",
      title: "דרגת זיהום",
      value: leasingData?.carZone?.spec?.pollutionGroup
        ? `${leasingData.carZone.spec.pollutionGroup}`
        : undefined,
    },
    {
      icon: "/icons/safety.svg",
      title: "ציון בטיחות",
      value: leasingData?.carZone?.spec?.safetyStarsNumber
        ? `${leasingData.carZone.spec.safetyStarsNumber}`
        : undefined,
    },
  ]
  return (
    <Container paddingMobile>
      <StyledContainer>
        {showSpinCar && detailedImages?.spinCarId ? (
          <SpinCarWidget spinCarId={detailedImages.spinCarId} />
        ) : leasingImage ? (
          <ImageContainer>
            <Image
              src={getNonUmbracoUrl(leasingImage)}
              alt=""
              fill
              style={{ objectFit: "cover" }}
            />
          </ImageContainer>
        ) : cu17Image?.url ? (
          <ImageContainer>
            <Image
              src={getUmbracoMediaUrl(cu17Image.url)}
              alt=""
              fill
              style={{ objectFit: "cover" }}
            />
          </ImageContainer>
        ) : null}

        <AccordionColumn>
          {leasingData && (
            <StyledAccordion
              expanded={expanded === "panel0"}
              onChange={handleChange("panel0")}
            >
              <AccordionSummary
                expandIcon={
                  <Image
                    src="/icons/accordionDown.svg"
                    alt=""
                    width={32}
                    height={32}
                  />
                }
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography
                  variant="body1"
                  fontWeight={expanded === "panel0" ? 600 : 400}
                >
                  פרטי הרכב
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CardsGrid secondHand={false}>
                  {items.map((el, index) => (
                    <Card key={index}>
                      <IconbBox>
                        <Image
                          src={getUmbracoMediaUrl(el.icon)}
                          alt=""
                          width={40}
                          height={40}
                        />
                      </IconbBox>
                      <CardTextBox>
                        <Typography variant="body3">{el.title}</Typography>
                        <Desktop centerHorizontally>
                          <Typography variant="body1" fontWeight={600}>
                            {el.value}
                          </Typography>
                        </Desktop>
                        <Mobile centerHorizontally>
                          <Typography variant="body2" fontWeight={600}>
                            {el.value}
                          </Typography>
                        </Mobile>
                      </CardTextBox>
                    </Card>
                  ))}
                </CardsGrid>
              </AccordionDetails>
            </StyledAccordion>
          )}
          <StyledAccordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
          >
            <AccordionSummary
              expandIcon={
                <Image
                  src="/icons/accordionDown.svg"
                  alt=""
                  width={32}
                  height={32}
                />
              }
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography
                variant="body1"
                fontWeight={expanded === "panel1" ? 600 : 400}
              >
                {vehicleInfoBlockMapped?.[0]?.infoTitle}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AccordionItemsColumns>
                {q1.map((item, i) => (
                  <AccordionItem key={i}>
                    <Image
                      src="/icons/check_small.svg"
                      alt=""
                      width={24}
                      height={24}
                    />
                    <Typography variant="body2">{item}</Typography>
                  </AccordionItem>
                ))}
                {vehicleInfoBlockMapped?.[0]?.disclaimer && (
                  <Typography variant="body2">
                    {vehicleInfoBlockMapped?.[0]?.disclaimer}
                  </Typography>
                )}
              </AccordionItemsColumns>
            </AccordionDetails>
          </StyledAccordion>
          <StyledAccordion
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
          >
            <AccordionSummary
              expandIcon={
                <Image
                  src="/icons/accordionDown.svg"
                  alt=""
                  width={32}
                  height={32}
                />
              }
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Typography
                variant="body1"
                fontWeight={expanded === "panel2" ? 600 : 400}
              >
                {vehicleInfoBlockMapped?.[1]?.infoTitle}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AccordionItemsColumns>
                {q2.map((item, i) => (
                  <AccordionItem key={i}>
                    <Image
                      src="/icons/check_small.svg"
                      alt=""
                      width={24}
                      height={24}
                    />
                    <Typography variant="body2">{item}</Typography>
                  </AccordionItem>
                ))}
                {vehicleInfoBlockMapped?.[1]?.disclaimer && (
                  <Typography variant="body2">
                    {vehicleInfoBlockMapped?.[1]?.disclaimer}
                  </Typography>
                )}
              </AccordionItemsColumns>
            </AccordionDetails>
          </StyledAccordion>
          <StyledAccordion
            expanded={expanded === "panel3"}
            onChange={handleChange("panel3")}
          >
            <AccordionSummary
              expandIcon={
                <Image
                  src="/icons/accordionDown.svg"
                  alt=""
                  width={32}
                  height={32}
                />
              }
              aria-controls="panel3-content"
              id="panel3-header"
            >
              <Typography
                variant="body1"
                fontWeight={expanded === "panel3" ? 600 : 400}
              >
                {vehicleInfoBlockMapped?.[2]?.infoTitle}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AccordionItemsColumns>
                {q3.map((item, i) => (
                  <AccordionItem key={i}>
                    <Image
                      src="/icons/check_small.svg"
                      alt=""
                      width={24}
                      height={24}
                    />
                    <Typography variant="body2">{item}</Typography>
                  </AccordionItem>
                ))}
                {vehicleInfoBlockMapped?.[2]?.disclaimer && (
                  <Typography variant="body2">
                    {vehicleInfoBlockMapped?.[2]?.disclaimer}
                  </Typography>
                )}
              </AccordionItemsColumns>
            </AccordionDetails>
          </StyledAccordion>
        </AccordionColumn>
      </StyledContainer>
    </Container>
  )
}
